import { useState, useRef, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { MdPhotoCamera, MdClose, MdRemove, MdAdd, MdVisibility } from 'react-icons/md';
import api from '../services/api';
import getCroppedImg from '../utils/cropImage';

const initialsOf = (name) =>
  (name || '?').trim().split(/\s+/).slice(0, 2).map(w => w[0]?.toUpperCase()).join('') || '?';

/**
 * Reusable circular avatar with click-to-upload + crop.
 * Props: currentPhotoUrl, studentName, uploadUrl, onUploaded, size (px, default 96).
 * The caller supplies uploadUrl (e.g. /api/students/:id/photo); POSTs field "photo".
 */
export default function AvatarUploader({ currentPhotoUrl, studentName, uploadUrl, onUploaded, size = 96 }) {
  const fileRef = useRef(null);
  const [imageSrc, setImageSrc] = useState(null); // data URL of the chosen file (modal open when set)
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [viewing, setViewing] = useState(false); // full-size lightbox

  const onCropComplete = useCallback((_area, areaPixels) => setCroppedAreaPixels(areaPixels), []);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file later
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setError('');
    };
    reader.readAsDataURL(file);
  };

  const closeModal = () => { if (!uploading) { setImageSrc(null); setError(''); } };

  const handleSave = async () => {
    if (!croppedAreaPixels) return;
    setUploading(true);
    setError('');
    try {
      const blob = await getCroppedImg(imageSrc, croppedAreaPixels, 400);
      const fd = new FormData();
      fd.append('photo', blob, 'avatar.jpg'); // backend expects field name exactly "photo"
      // Let the browser/axios set the multipart Content-Type (with boundary) automatically.
      const res = await api.post(uploadUrl, fd);
      const url = res.data?.profilePhoto;
      if (onUploaded) onUploaded(url);
      setImageSrc(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      {/* Circular avatar with hover actions */}
      <div
        style={{ width: size, height: size }}
        className="group relative rounded-full overflow-hidden flex-shrink-0"
      >
        {currentPhotoUrl ? (
          <img src={currentPhotoUrl} alt={studentName || 'Avatar'} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-primary-light text-primary flex items-center justify-center font-bold"
            style={{ fontSize: size * 0.36 }}>
            {initialsOf(studentName)}
          </div>
        )}
        {/* Hover overlay — View (if a photo exists) + Change */}
        <div className="absolute inset-0 bg-black/45 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center gap-1.5 text-white">
          {currentPhotoUrl && (
            <button
              type="button"
              onClick={() => setViewing(true)}
              title="View photo"
              className="w-7 h-7 flex items-center justify-center rounded-full bg-white/15 hover:bg-white/30 transition-colors focus:outline-none"
            >
              <MdVisibility className="text-base" />
            </button>
          )}
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            title="Change photo"
            className="w-7 h-7 flex items-center justify-center rounded-full bg-white/15 hover:bg-white/30 transition-colors focus:outline-none"
          >
            <MdPhotoCamera className="text-base" />
          </button>
        </div>
      </div>

      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleFile}
        className="hidden"
      />

      {/* Full-size view (lightbox) */}
      {viewing && currentPhotoUrl && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4"
          onClick={(e) => e.target === e.currentTarget && setViewing(false)}>
          <div className="relative">
            <img
              src={currentPhotoUrl}
              alt={studentName || 'Avatar'}
              className="max-w-[400px] w-full max-h-[80vh] object-contain rounded-2xl shadow-2xl"
            />
            <button
              onClick={() => setViewing(false)}
              title="Close"
              className="absolute -top-3 -right-3 w-8 h-8 flex items-center justify-center rounded-full bg-white text-gray-700 shadow-lg hover:bg-gray-100"
            >
              <MdClose className="text-lg" />
            </button>
          </div>
        </div>
      )}

      {/* Crop modal */}
      {imageSrc && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
          onClick={(e) => e.target === e.currentTarget && closeModal()}>
          <div className="w-full max-w-sm bg-white rounded-2xl border border-divider shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
              <h2 className="font-bold text-secondary text-sm">Adjust your photo</h2>
              <button onClick={closeModal} disabled={uploading}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 disabled:opacity-50">
                <MdClose className="text-xl" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              {/* Cropper needs a positioned parent */}
              <div className="relative w-full rounded-xl overflow-hidden bg-gray-900" style={{ height: 280 }}>
                <Cropper
                  image={imageSrc}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  cropShape="round"
                  showGrid={false}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                />
              </div>

              {/* Zoom control */}
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">Zoom</label>
                <div className="flex items-center gap-2">
                  <MdRemove className="text-gray-400 text-base flex-shrink-0" />
                  <input
                    type="range" min={1} max={3} step={0.1} value={zoom}
                    onChange={(e) => setZoom(Number(e.target.value))}
                    className="w-full accent-primary"
                  />
                  <MdAdd className="text-gray-400 text-base flex-shrink-0" />
                </div>
              </div>

              {error && <p className="text-xs text-red-500">{error}</p>}

              <div className="flex justify-end gap-2 pt-1">
                <button onClick={closeModal} disabled={uploading}
                  className="px-4 py-2 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50">
                  Cancel
                </button>
                <button onClick={handleSave} disabled={uploading || !croppedAreaPixels}
                  className="px-4 py-2 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-60 flex items-center gap-2">
                  {uploading && <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                  {uploading ? 'Saving…' : 'Save photo'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
